import { Injectable } from '@angular/core';
import { EPlatformFeature } from '@enums/campaign.enum';
import { ICampaignAnalytics, TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { ICreator } from '@pages/creators/creators.interface';
import { upperFirst } from 'lodash';
import { ECampaignDetailsTab } from '../../campaign-details.interface';
import {
  ICampaignExportApi,
  ICampaignExportCreatorApi,
  ICampaignExportKpiApi,
  ICampaignExportPlatformFeatureApi,
  ICampaignExportTabApi,
} from './campaign-export-dialog.interface';

@Injectable({
  providedIn: 'root',
})
export class CampaignExportDialogService {
  public getCreators(campaignAnalytics: ICampaignAnalytics): ICreator[] {
    const creators: ICreator[] = [];

    campaignAnalytics.creators.forEach((creator) => {
      creators.push({ icon: creator.icon, id: creator.id, name: creator.name });
    });

    return creators.sort((a, b) => a.name.localeCompare(b.name));
  }

  public getSelectedPlatformFeatures(
    campaignExport: ICampaignExportApi | null,
    selectedPlatformFeatures: Set<EPlatformFeature>,
    allPlatformFeaturesSelected: boolean,
  ): ICampaignExportPlatformFeatureApi[] {
    if (allPlatformFeaturesSelected) {
      return [];
    }

    if (!campaignExport) {
      return Array.from(selectedPlatformFeatures).map((platformFeature) => ({
        platformFeature: upperFirst(platformFeature) as EPlatformFeature,
      }));
    }

    const platformFeatures: ICampaignExportPlatformFeatureApi[] = [];

    selectedPlatformFeatures.forEach((platformFeature) => {
      const existingPlatformFeature = campaignExport?.platformFeatures?.find(
        (pf) => pf.platformFeature === platformFeature,
      );

      platformFeatures.push({
        id: existingPlatformFeature?.id || null,
        platformFeature: upperFirst(platformFeature) as EPlatformFeature,
      });
    });

    return platformFeatures;
  }

  public getSelectedKpis(
    campaignExport: ICampaignExportApi | null,
    selectedKpis: Set<TExpectedKpi>,
    allKpisSelected: boolean,
  ): ICampaignExportKpiApi[] {
    if (allKpisSelected) {
      return [];
    }

    if (!campaignExport) {
      return Array.from(selectedKpis).map((kpi) => ({ kpi: kpi as TExpectedKpi }));
    }

    const kpis: ICampaignExportKpiApi[] = [];

    selectedKpis.forEach((kpi) => {
      const existingKpi = campaignExport?.kpis?.find((kp) => kp.kpi === (kpi as TExpectedKpi));

      kpis.push({ id: existingKpi?.id || null, kpi: kpi as TExpectedKpi });
    });

    return kpis;
  }

  public getSelectedCreators(
    campaignExport: ICampaignExportApi | null,
    selectedCreatorIds: Set<string>,
    allCreatorsSelected: boolean,
  ): ICampaignExportCreatorApi[] {
    if (allCreatorsSelected) {
      return [];
    }

    if (!campaignExport) {
      return Array.from(selectedCreatorIds).map((creatorId) => ({ creatorId }));
    }

    const creators: ICampaignExportCreatorApi[] = [];

    selectedCreatorIds.forEach((creatorId) => {
      const existingCreator = campaignExport?.creators?.find((creator) => creator.creatorId === creatorId);

      creators.push({ creatorId, id: existingCreator?.id || null });
    });

    return creators;
  }

  public getSelectedTabs(
    campaignExport: ICampaignExportApi | null,
    selectedTabs: Set<ECampaignDetailsTab>,
    allTabsSelected: boolean,
  ): ICampaignExportTabApi[] {
    if (allTabsSelected) {
      return [];
    }

    if (!campaignExport) {
      return Array.from(selectedTabs).map((tab) => ({ tab }));
    }

    const tabs: ICampaignExportTabApi[] = [];

    selectedTabs.forEach((tab) => {
      const existingTab = campaignExport?.tabs?.find((t) => t.tab === tab);

      tabs.push({ id: existingTab?.id || null, tab });
    });

    return tabs;
  }
}
